import type { FormField, PublicForm } from '@pm/schemas';
import { formatPhone } from '@pm/schemas';
import { Button, Checkbox, Field, Input, Select, Skeleton, Textarea } from '@pm/ui';
import { CheckCircle2, Send } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { str } from '@/lib/format';

type Answers = Record<string, unknown>;
interface Issue {
  name: string;
  message: string;
}

const API = '/api/v1/public/forms';

/**
 * Página pública do formulário (FRONTEND_UX §11): sem layout autenticado, sem prioridade.
 * Usa fetch puro: não há sessão nem refresh aqui.
 */
export function PublicFormPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const token = params.get('token');
  const q = token ? `?token=${encodeURIComponent(token)}` : '';
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'error'; code: string; message: string }
    | { status: 'ready'; form: PublicForm }
    | { status: 'done'; message: string }
  >({ status: 'loading' });

  useEffect(() => {
    let alive = true;
    void fetch(`${API}/${encodeURIComponent(slug)}${q}`)
      .then(async (res) => {
        const body = (await res.json()) as {
          data?: PublicForm;
          error?: { code: string; message: string };
        };
        if (!alive) return;
        if (!res.ok || !body.data)
          setState({
            status: 'error',
            code: body.error?.code ?? 'ERROR',
            message: body.error?.message ?? 'Não foi possível carregar o formulário.',
          });
        else setState({ status: 'ready', form: body.data });
      })
      .catch(
        () =>
          alive &&
          setState({ status: 'error', code: 'NETWORK', message: 'Sem conexão. Tente novamente.' }),
      );
    return () => {
      alive = false;
    };
  }, [slug, q]);

  return (
    <div
      className="min-h-screen bg-bg px-4 py-10 text-text-1"
      style={{
        backgroundImage:
          'radial-gradient(700px 500px at 100% 0%, color-mix(in oklab, var(--accent) 8%, transparent), transparent 70%)',
      }}
    >
      <main className="mx-auto w-full max-w-[640px]">
        {state.status === 'loading' ? (
          <div className="flex flex-col gap-3 rounded-lg border border-border bg-surface-1 p-6">
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="w-3/4" />
            <Skeleton className="h-24 rounded-md" />
          </div>
        ) : null}
        {state.status === 'error' ? (
          <div className="rounded-lg border border-border bg-surface-1 p-6">
            <p className="font-mono text-xs uppercase tracking-[var(--tracking-caps)] text-text-3">
              {state.code}
            </p>
            <h1 className="mt-1 text-lg font-medium">
              {state.code === 'FORM_TOKEN_REQUIRED' || state.code === 'FORM_TOKEN_INVALID'
                ? 'Este link precisa de um token válido'
                : 'Formulário indisponível'}
            </h1>
            <p className="mt-2 text-sm text-text-2">{state.message}</p>
          </div>
        ) : null}
        {state.status === 'done' ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-border bg-surface-1 p-8 text-center">
            <CheckCircle2 className="size-8 text-success" />
            <h1 className="text-lg font-medium">Solicitação enviada</h1>
            <p className="text-sm text-text-2">{state.message}</p>
          </div>
        ) : null}
        {state.status === 'ready' ? (
          <PublicFormBody
            form={state.form}
            slug={slug}
            q={q}
            onDone={(message) => setState({ status: 'done', message })}
          />
        ) : null}
      </main>
    </div>
  );
}

function PublicFormBody({
  form,
  slug,
  q,
  onDone,
}: {
  form: PublicForm;
  slug: string;
  q: string;
  onDone: (message: string) => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [answers, setAnswers] = useState<Answers>({});
  const [issues, setIssues] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const set = (n: string, v: unknown) => setAnswers((a) => ({ ...a, [n]: v }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const local: Record<string, string> = {};
    if (name.trim().length < 2) local.__name = 'Informe seu nome.';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) local.__email = 'Informe um e-mail válido.';
    for (const f of form.fields) {
      const v = answers[f.name];
      if (
        f.required &&
        (v === undefined || v === '' || v === null || (Array.isArray(v) && v.length === 0))
      )
        local[f.name] = 'Campo obrigatório.';
    }
    setIssues(local);
    if (Object.keys(local).length) return;
    setSubmitting(true);
    setFormError(null);
    try {
      const res = await fetch(`${API}/${encodeURIComponent(slug)}/submit${q}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'idempotency-key': idempotencyKey },
        body: JSON.stringify({ requester: { name, email, phone: phone || null }, answers }),
      });
      const body = (await res.json()) as {
        data?: { message: string };
        error?: { code: string; message: string; details?: { fields?: Issue[] } };
      };
      if (res.ok && body.data) return onDone(body.data.message);
      if (body.error?.details?.fields)
        setIssues(Object.fromEntries(body.error.details.fields.map((i) => [i.name, i.message])));
      setFormError(
        res.status === 429
          ? 'Muitos envios em pouco tempo. Aguarde um instante.'
          : (body.error?.message ?? 'Não foi possível enviar.'),
      );
    } catch {
      setFormError('Sem conexão. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={(e) => void submit(e)}
      className="flex flex-col gap-5 rounded-lg border border-border bg-surface-1 p-6"
      noValidate
    >
      <header>
        <p className="font-mono text-xs uppercase tracking-[var(--tracking-caps)] text-text-3">
          {form.organization_name}
        </p>
        <h1 className="mt-1 text-xl font-medium">{form.name}</h1>
        {form.description ? <p className="mt-1 text-sm text-text-2">{form.description}</p> : null}
      </header>
      <fieldset className="flex flex-col gap-3">
        <legend className="mb-1 font-mono text-xs uppercase tracking-[var(--tracking-caps)] text-text-3">
          Seus dados
        </legend>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Nome *" htmlFor="pf-name" error={issues.__name}>
            <Input
              id="pf-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
            />
          </Field>
          <Field label="E-mail *" htmlFor="pf-email" error={issues.__email}>
            <Input
              id="pf-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </Field>
        </div>
        <Field label="Telefone / WhatsApp" htmlFor="pf-phone">
          <Input
            id="pf-phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(formatPhone(e.target.value))}
            placeholder="(11) 98765-4321"
            maxLength={19}
            autoComplete="tel"
            mono
          />
        </Field>
      </fieldset>
      {form.fields.length > 0 ? (
        <fieldset className="flex flex-col gap-3">
          <legend className="mb-1 font-mono text-xs uppercase tracking-[var(--tracking-caps)] text-text-3">
            Solicitação
          </legend>
          {form.fields.map((f) => (
            <FieldInput
              key={f.id}
              field={f}
              value={answers[f.name]}
              onChange={(v) => set(f.name, v)}
              error={issues[f.name]}
            />
          ))}
        </fieldset>
      ) : null}
      {formError ? (
        <p className="rounded border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
          {formError}
        </p>
      ) : null}
      <Button type="submit" variant="primary" loading={submitting} className="self-end">
        <Send /> Enviar solicitação
      </Button>
    </form>
  );
}

function FieldInput({
  field,
  value,
  onChange,
  error,
}: {
  field: FormField;
  value: unknown;
  onChange: (v: unknown) => void;
  error?: string;
}) {
  const id = `pf-${field.name}`;
  const label = `${field.label}${field.required ? ' *' : ''}`;
  const text = str(value);
  switch (field.field_type) {
    case 'TEXTAREA':
      return (
        <Field label={label} htmlFor={id} hint={field.help_text ?? undefined} error={error}>
          <Textarea
            id={id}
            value={text}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder ?? undefined}
            className="min-h-[96px]"
          />
        </Field>
      );
    case 'BOOLEAN':
      return (
        <div className="flex flex-col gap-1">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={value === true} onCheckedChange={(v) => onChange(v === true)} />{' '}
            {label}
          </label>
          {field.help_text ? <span className="text-xs text-text-3">{field.help_text}</span> : null}
          {error ? <span className="text-xs text-danger">{error}</span> : null}
        </div>
      );
    case 'SELECT':
      return (
        <Field label={label} htmlFor={id} hint={field.help_text ?? undefined} error={error}>
          <Select id={id} value={text} onChange={(e) => onChange(e.target.value || undefined)}>
            <option value="">{field.placeholder ?? 'Selecione…'}</option>
            {field.options.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </Select>
        </Field>
      );
    case 'MULTISELECT': {
      const arr = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">{label}</span>
          <div className="flex flex-wrap gap-2">
            {field.options.map((o) => (
              <label
                key={o}
                className="flex items-center gap-2 rounded border border-border px-2 py-1 text-sm"
              >
                <Checkbox
                  checked={arr.includes(o)}
                  onCheckedChange={(v) => onChange(v ? [...arr, o] : arr.filter((x) => x !== o))}
                />
                {o}
              </label>
            ))}
          </div>
          {field.help_text ? <span className="text-xs text-text-3">{field.help_text}</span> : null}
          {error ? <span className="text-xs text-danger">{error}</span> : null}
        </div>
      );
    }
    case 'NUMBER':
    case 'CURRENCY':
      return (
        <Field label={label} htmlFor={id} hint={field.help_text ?? undefined} error={error}>
          <Input
            id={id}
            type="number"
            step={field.field_type === 'CURRENCY' ? '0.01' : 'any'}
            mono
            value={text}
            onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)}
            placeholder={field.placeholder ?? undefined}
          />
        </Field>
      );
    case 'DATE':
      return (
        <Field label={label} htmlFor={id} hint={field.help_text ?? undefined} error={error}>
          <Input
            id={id}
            type="date"
            mono
            value={text}
            onChange={(e) => onChange(e.target.value || undefined)}
          />
        </Field>
      );
    case 'DATETIME':
      return (
        <Field label={label} htmlFor={id} hint={field.help_text ?? undefined} error={error}>
          <Input
            id={id}
            type="datetime-local"
            mono
            value={text}
            onChange={(e) =>
              onChange(e.target.value ? new Date(e.target.value).toISOString() : undefined)
            }
          />
        </Field>
      );
    default:
      return (
        <Field label={label} htmlFor={id} hint={field.help_text ?? undefined} error={error}>
          <Input
            id={id}
            type={
              field.field_type === 'EMAIL'
                ? 'email'
                : field.field_type === 'URL'
                  ? 'url'
                  : field.field_type === 'PHONE'
                    ? 'tel'
                    : 'text'
            }
            value={text}
            onChange={(e) =>
              onChange(field.field_type === 'PHONE' ? formatPhone(e.target.value) : e.target.value)
            }
            placeholder={field.placeholder ?? undefined}
            mono={field.field_type === 'PHONE'}
          />
        </Field>
      );
  }
}
